import React from 'react'
import { Link } from 'react-router-dom';
import Slider from "react-slick";
import ProductImage, { PRODUCT_IMAGE_PLACEHOLDER } from "./common/ProductImage";
import { getBannerContent, getBannerId, getBannerImage, getCellContents, getValue, hasBannerContent, toRenderableText } from "../utils/bannerContent";
import "./banner-responsive.css";

const FALLBACK_BANNER_IMAGE = "assets/images/bg/next-day-order-banner.png";
const failedBannerImageUrls = new Set();

const getBrowserImageUrl = (value) => {
    const path = String(value || "").trim();

    if (!path) return "";
    if (typeof window === "undefined" || !window.location) return path;

    try {
        return new URL(path, window.location.origin).href;
    } catch (error) {
        return path;
    }
};

const rememberFailedBannerImage = (value) => {
    const path = String(value || "").trim();
    const browserUrl = getBrowserImageUrl(path);

    if (path) failedBannerImageUrls.add(path);
    if (browserUrl) failedBannerImageUrls.add(browserUrl);
};

const isFailedBannerImage = (value) => {
    const path = String(value || "").trim();
    const browserUrl = getBrowserImageUrl(path);

    return Boolean(path && (failedBannerImageUrls.has(path) || failedBannerImageUrls.has(browserUrl)));
};

const BannerActionLink = ({ href, className, children, style }) => {
    if (!href) {
        return (
            <span className={className} style={style}>
                {children}
            </span>
        );
    }

    if (/^https?:\/\//i.test(href)) {
        return (
            <a className={className} href={href} rel="noreferrer" target="_blank" style={style}>
                {children}
            </a>
        );
    }

    return (
        <Link className={className} to={href.startsWith("/") ? href : `/${href}`} style={style}>
            {children}
        </Link>
    );
};

const hasFailedBannerImage = (src, mobileSrc, tabletSrc, desktopSrc) =>
    [src, mobileSrc, tabletSrc, desktopSrc].some(isFailedBannerImage);

const BannerImage = ({
    src,
    imageSet,
    alt,
    className,
    loading,
    fetchPriority,
    objectPosition,
    responsive = false,
}) => {
    const fallbackSrc = PRODUCT_IMAGE_PLACEHOLDER;
    const mobileSrc = imageSet?.mobile;
    const tabletSrc = imageSet?.tablet;
    const desktopSrc = imageSet?.desktop;
    const [useFallback, setUseFallback] = React.useState(() =>
        hasFailedBannerImage(src, mobileSrc, tabletSrc, desktopSrc)
    );

    React.useEffect(() => {
        setUseFallback(hasFailedBannerImage(src, mobileSrc, tabletSrc, desktopSrc));
    }, [desktopSrc, mobileSrc, src, tabletSrc]);

    const handleError = (event) => {
        const failedSrc = event.currentTarget.currentSrc || event.currentTarget.src || src;

        rememberFailedBannerImage(failedSrc);
        rememberFailedBannerImage(event.currentTarget.getAttribute("src"));

        if (event.currentTarget.parentElement?.tagName?.toLowerCase() === "picture") {
            event.currentTarget.parentElement
                .querySelectorAll("source")
                .forEach((source) => source.removeAttribute("srcset"));
        }

        event.currentTarget.removeAttribute("srcset");
        event.currentTarget.removeAttribute("sizes");

        if (useFallback) {
            event.currentTarget.onerror = null;
            return;
        }

        event.currentTarget.src = fallbackSrc;
        setUseFallback(true);
    };

    const activeSrc = useFallback ? fallbackSrc : (src || fallbackSrc);
    const imgProps = {
        src: activeSrc,
        alt,
        className,
        loading,
        decoding: "async",
        fetchPriority,
        onError: handleError,
        fallbackSrc,
        ...(objectPosition && { style: { objectPosition } }),
    };

    if (!responsive) {
        return <ProductImage {...imgProps} />;
    }

    const shouldUseResponsiveSources =
        !useFallback && mobileSrc && tabletSrc && desktopSrc;

    return (
        <picture className="banner-json-slide__media">
            {shouldUseResponsiveSources && (
                <>
                    <source media="(max-width: 575px)" srcSet={mobileSrc} />
                    <source media="(max-width: 991px)" srcSet={tabletSrc} />
                </>
            )}
            <ProductImage
                {...imgProps}
                {...(shouldUseResponsiveSources && {
                    srcSet: `${mobileSrc} 575w, ${tabletSrc} 991w, ${desktopSrc} 1440w`,
                    sizes: "100vw",
                })}
            />
        </picture>
    );
};

const Banner = ({ banners, loading = false }) => {
    const bannerItems = getCellContents(banners);
    const slides = bannerItems.length ? bannerItems : [FALLBACK_BANNER_IMAGE];
    const hasMultipleBanners = slides.length > 1;
    const settings = {
        dots: hasMultipleBanners,
        arrows: false,
        infinite: hasMultipleBanners,
        autoplay: false,
        autoplaySpeed: 0,
        speed: 700,
        slidesToShow: 1,
        slidesToScroll: 1,
        pauseOnHover: false,
        pauseOnFocus: false,
        swipeToSlide: true,
        cssEase: "ease-in-out",
        customPaging: (index) => (
            <button type="button" aria-label={`Go to banner ${index + 1}`} />
        ),
    };

    return (
        <div className={`banner banner-image-style ${loading ? "is-loading" : ""}`}>
            <div className="container container-lg">
                <div className="banner-image-card banner-image-carousel">
                    <Slider
                        {...settings}
                        key={slides.map((banner, index) => getBannerId(banner, index)).join("-")}
                        className="banner-image-slider"
                    >
                        {slides.map((banner, index) => {
                            const bannerContent = getBannerContent(banner, index);
                            const showJsonBanner = hasBannerContent(bannerContent);
                            const bannerImage = showJsonBanner
                                ? bannerContent.imageSet.desktop
                                : getBannerImage(banner);
                            const bannerAlt = toRenderableText(
                                bannerContent.mediaAlt ||
                                getValue(
                                    bannerContent,
                                    ["title", "banner_title", "bannerTitle", "name", "heading"],
                                    "Vegetables and Fruits - Next Day Order prices are subject to change according to market price fluctuations"
                                ),
                                "Vegetables and Fruits - Next Day Order prices are subject to change according to market price fluctuations"
                            );

                            if (showJsonBanner) {
                                const bannerTitle = toRenderableText(bannerContent.title, "");
                                const bannerSubtitle = toRenderableText(bannerContent.subtitle, "");
                                const bannerEyebrow = toRenderableText(bannerContent.eyebrow, "");
                                const bannerDiscountText = toRenderableText(bannerContent.discountText, "");
                                const bannerButtonLabel = toRenderableText(bannerContent.buttonLabel, "");

                                return (
                                    <div className="banner-image-slide" key={getBannerId(bannerContent, index)}>
                                        <div
                                            className="banner-json-slide"
                                            data-layout={bannerContent.style.layout}
                                            style={{
                                                "--banner-json-bg": bannerContent.style.background,
                                                "--banner-json-title": bannerContent.style.titleColor,
                                                "--banner-json-subtitle": bannerContent.style.subtitleColor,
                                                "--banner-json-eyebrow": bannerContent.style.eyebrowColor,
                                                "--banner-json-accent": bannerContent.style.accentColor,
                                                "--banner-json-overlay-opacity": bannerContent.style.overlayOpacity,
                                                ...(bannerContent.responsiveRatios.desktop && {
                                                    "--banner-json-ratio-desktop": bannerContent.responsiveRatios.desktop,
                                                }),
                                                ...(bannerContent.responsiveRatios.tablet && {
                                                    "--banner-json-ratio-tablet": bannerContent.responsiveRatios.tablet,
                                                }),
                                                ...(bannerContent.responsiveRatios.mobile && {
                                                    "--banner-json-ratio-mobile": bannerContent.responsiveRatios.mobile,
                                                }),
                                            }}
                                        >
                                            <BannerImage
                                                responsive
                                                src={bannerImage}
                                                imageSet={bannerContent.imageSet}
                                                alt={bannerAlt}
                                                className="banner-image-card__img banner-json-slide__img"
                                                loading={index === 0 ? "eager" : "lazy"}
                                                fetchPriority={index === 0 ? "high" : "auto"}
                                                objectPosition={bannerContent.imageSet.position}
                                            />
                                            <div className="banner-json-slide__content">
                                                {bannerEyebrow && (
                                                    <span className="banner-json-slide__eyebrow">
                                                        {bannerEyebrow}
                                                    </span>
                                                )}
                                                {bannerTitle && (
                                                    <h2>{bannerTitle}</h2>
                                                )}
                                                {bannerSubtitle && (
                                                    <p>{bannerSubtitle}</p>
                                                )}
                                                <div className="banner-json-slide__actions">
                                                    {bannerDiscountText && (
                                                        <span
                                                            className="banner-json-slide__badge"
                                                            style={{
                                                                background: bannerContent.discountStyle.background || undefined,
                                                                color: bannerContent.discountStyle.color || undefined,
                                                            }}
                                                        >
                                                            {bannerDiscountText}
                                                        </span>
                                                    )}
                                                    {bannerButtonLabel && (
                                                        <BannerActionLink
                                                            className="banner-json-slide__button"
                                                            href={bannerContent.href}
                                                            style={{
                                                                background: bannerContent.buttonStyle.background || undefined,
                                                                color: bannerContent.buttonStyle.color || undefined,
                                                            }}
                                                        >
                                                            {bannerButtonLabel}
                                                        </BannerActionLink>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div className="banner-image-slide" key={getBannerId(banner, index)}>
                                    <BannerImage
                                        src={bannerImage}
                                        alt={bannerAlt}
                                        className="banner-image-card__img"
                                        loading={index === 0 ? "eager" : "lazy"}
                                        fetchPriority={index === 0 ? "high" : "auto"}
                                    />
                                </div>
                            );
                        })}
                    </Slider>
                </div>
            </div>
        </div>
    );
}

export default Banner
